import React, { useEffect, useState } from "react";
import {
    TextField,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    useTheme,
    MenuItem,
    IconButton,
    FormControlLabel,
    Checkbox,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { addProperty, updateProperty } from "state/apis";
import { toast } from "react-toastify";
import Autocomplete from "@mui/material/Autocomplete";
import { Close as CloseIcon } from "@mui/icons-material";
import moment from "moment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { add } from "dayjs";

const AddPropertyForm = ({ open, onClose, onSubmit, editData, editMode, owners }) => {
    const theme = useTheme();
    const [data, setData] = useState({
        propertyName: "",
        phone: "",
        ownerId: "",
        type: "",
        image: "",
        address: "",
        docs: [],
        serialNumber: "",
        isSold: false,
        soldDate: "",
    });

    useEffect(() => {
        if (editMode && editData) {
            setData((prevData) => ({
                ...prevData,
                propertyName: editData.propertyName,
                phone: editData.phone,
                ownerId: editData.ownerId,
                address: editData.address,
                type: editData.type,
                image: editData.image,
                serialNumber: editData.serialNumber,
                isSold: editData.isSold,
            }));
        } else {
            setData({
                propertyName: "",
                phone: "",
                ownerId: "",
                type: "",
                image: "",
                address: "",
                docs: [],
                serialNumber: "",
                isSold: false,
                soldDate: "",
            });
        }
    }, [editData, editMode]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    };

    const handleOwnerChange = (newValue) => {
        setData((prevData) => ({
            ...prevData,
            ownerId: newValue ? newValue.id : null,
        }));
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        setData((prevData) => ({
            ...prevData,
            image: file,
        }));
    };

    const handleAddDocument = () => {
        setData((prevData) => ({
            ...prevData,
            docs: [...prevData.docs, { name: "", file: null }],
        }));
    };

    const handleDocumentNameChange = (index, value) => {
        const updatedDocuments = [...data.docs];
        updatedDocuments[index].name = value;
        setData((prevData) => ({
            ...prevData,
            docs: updatedDocuments,
        }));
    };

    const handleCancelDocument = (indexToRemove) => {
        setData((prevState) => ({
            ...prevState,
            docs: prevState.docs.filter((_, index) => index !== indexToRemove),
        }));
    };
    const handleDocumentFileChange = (index, file) => {
        const updatedDocuments = [...data.docs];
        updatedDocuments[index].file = file;
        setData((prevData) => ({
            ...prevData,
            docs: updatedDocuments,
        }));
    };

    const handleDateChange = (date) => {
        const data = date;
        const formattedDate = data.format("YYYY-MM-DD");

        setData((prevData) => ({
            ...prevData,
            soldDate: formattedDate,
        }));
    };

    const handleSubmit = async () => {
        try {
            if (editMode) {
                const response = await updateProperty(editData.id, data);
                toast.success(response.message);
            } else {
                const response = await addProperty(data);
                toast.success(response.message);
            }
            onSubmit();
            onClose();
        } catch (error) {
            console.error("Error:", error);
            if (error.response) {
                toast.error(error.response.data.message);
            } else if (error.request) {
                toast.error("No response received from server.");
            } else {
                toast.error("An error occurred. Please try again.");
            }
        }
    };
    return (
        <Dialog open={open} onClose={onClose}>
            {/* <DialogTitle sx={{ textAlign: "center", fontWeight: "900", mb: "2px" }}>
                {editMode ? "Edit Property" : "Add Property"}
            </DialogTitle> */}
            <DialogTitle
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    fontWeight: "bold",
                    marginLeft: "6px",
                }}
            >
                {editMode ? "Edit Property" : "Add Property"}
                <IconButton onClick={onClose} sx={{ color: "inherit" }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <Grid container spacing={3} p={"6px"}>
                    <Grid item xs={12}>
                        <TextField
                            color="primary"
                            fullWidth
                            label="Serial Number"
                            name="serialNumber"
                            value={data?.serialNumber}
                            onChange={handleChange}
                            type="number"
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Autocomplete
                            disablePortal
                            id="combo-box-demo"
                            options={owners}
                            getOptionLabel={(option) => option.name}
                            value={
                                data.ownerId
                                    ? owners.find((owner) => owner.id === data.ownerId)
                                    : null
                            }
                            onChange={(event, newValue) => handleOwnerChange(newValue)}
                            isOptionEqualToValue={(option, value) => option === value}
                            renderOption={(props, option) => (
                                <li {...props} key={option.id}>
                                    {option.name}
                                </li>
                            )}
                            sx={{
                                width: "100%",
                                "& .MuiOutlinedInput-root": {
                                    "&.Mui-focused fieldset": {
                                        borderColor: "primary.main",
                                    },
                                },
                                "& .MuiInputLabel-root": {
                                    "&.Mui-focused": {
                                        color: "primary.main",
                                    },
                                },
                            }}
                            renderInput={(params) => (
                                <TextField {...params} fullWidth label="Select Owner" />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            color="primary"
                            fullWidth
                            label="Property Name"
                            name="propertyName"
                            value={data?.propertyName}
                            onChange={handleChange}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            color="primary"
                            fullWidth
                            label="Address"
                            name="address"
                            value={data?.address}
                            onChange={handleChange}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            color="primary"
                            fullWidth
                            label="Phone"
                            name="phone"
                            value={data?.phone}
                            onChange={handleChange}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            select
                            color="primary"
                            fullWidth
                            label="Select Type"
                            name="type"
                            value={data.type}
                            onChange={handleChange}
                        >
                            <MenuItem value="Building">Building</MenuItem>
                            <MenuItem value="Villa">Villa</MenuItem>
                            <MenuItem value="Flat">Flat</MenuItem>
                            <MenuItem value="Apartment">Apartment</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid item xs={12}>
                        <label htmlFor="imageUpload">Upload Image:</label>
                        <br />
                        <br />
                        <input
                            id="imageUpload"
                            type="file"
                            accept="image/*"
                            onChange={handleImageChange}
                        />
                    </Grid>
                    <Grid item xs={12} m={2} border={1} p={2} sx={{ borderColor: "grey.400" }}>
                        <Grid container alignItems="center">
                            <Grid item xs={8}>
                                <label>Property Documents</label>
                            </Grid>
                            <Grid item xs={4} container justifyContent="flex-end">
                                <Button variant="contained" onClick={handleAddDocument}>
                                    Add Document
                                </Button>
                            </Grid>
                        </Grid>

                        <Grid item xs={12}>
                            {data.docs.map((document, index) => (
                                <Grid container spacing={2} key={index} p={1}>
                                    <Grid item xs={5}>
                                        <TextField
                                            fullWidth
                                            color="primary"
                                            label="Document Name"
                                            value={document.name}
                                            onChange={(e) =>
                                                handleDocumentNameChange(index, e.target.value)
                                            }
                                        />
                                    </Grid>
                                    <Grid item xs={5} margin={"auto"}>
                                        <input
                                            type="file"
                                            accept="application/pdf, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                            onChange={(e) =>
                                                handleDocumentFileChange(index, e.target.files[0])
                                            }
                                        />
                                    </Grid>
                                    <Grid item xs={2} container justify="end" margin="auto">
                                        <IconButton
                                            color="error"
                                            onClick={() => handleCancelDocument(index)}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </Grid>
                                </Grid>
                            ))}
                        </Grid>
                    </Grid>
                    {editMode && (
                        <Grid item xs={3}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={data.isSold}
                                        onChange={(e) =>
                                            setData({
                                                ...data,
                                                isSold: e.target.checked,
                                                // soldDate: moment().format("DD-MM-YYYY"),
                                            })
                                        }
                                        name="isSold"
                                        color="primary"
                                    />
                                }
                                label="Is Sold"
                            />
                        </Grid>
                    )}
                    {editMode && data.isSold && (
                        <Grid item xs={6}>
                            <LocalizationProvider dateAdapter={AdapterDayjs} color="primary">
                                <DatePicker
                                    label="Select sold date"
                                    name="soldDate"
                                    value={data?.soldDate ? dayjs(data?.soldDate) : null}
                                    onChange={handleDateChange}
                                    format="DD/MM/YYYY"
                                    sx={{
                                        width: "100%",
                                        "& .MuiOutlinedInput-root": {
                                            "&.Mui-focused fieldset": {
                                                borderColor: "primary.main",
                                            },
                                        },
                                        "& .MuiInputLabel-root": {
                                            "&.Mui-focused": {
                                                color: "primary.main",
                                            },
                                        },
                                    }}
                                />
                            </LocalizationProvider>
                        </Grid>
                    )}
                </Grid>

                <DialogActions>
                    {/* <Button
                        variant="contained"
                        onClick={onClose}
                        sx={{
                            color: theme.palette.primary,
                            backgroundColor: theme.palette.primary[800],
                        }}
                    >
                        Cancel
                    </Button> */}
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        sx={{
                            color: theme.palette.primary.light,
                            backgroundColor: theme.palette.secondary[200],
                        }}
                    >
                        {editMode ? "Update" : "Add"}
                    </Button>
                </DialogActions>
            </DialogContent>
        </Dialog>
    );
};

export default AddPropertyForm;
